import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useChartContext } from '../../../../charts/echarts/ChartProvider'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import ChartGraphic from '../../../../charts/echarts/ChartGraphic'

function PipelineChartGraphic ({
  pipeline,
  variables,
  valueAccessor,
  valueFormat,
  childrenItems,
  type: _type,
  ...seriesProps
}) {
  const { setSeriesLoading } = useChartContext()
  const { formatter } = useFormattingContext()
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)

  const type = _type || (childrenItems?.length > 1 ? 'group' : 'text')

  const graphicText = useMemo(() => {
    const value = get(data, valueAccessor)
    return value ? formatter(value, valueFormat) : null
  }, [data, valueAccessor, valueFormat, formatter])

  const graphicChildren = useMemo(() => {
    return childrenItems?.map((child, index) => {
      const value = get(data, child.accessor) ?? child.text
      return {
        ...child,
        type: child.type || 'text',
        style: {
          fontFamily: 'GothamPro, Gotham-Book, "Open Sans", Tahoma, "Segoe UI", sans-serif',
          fill: '#000',
          ...(child.style ?? {}),
          text: child.format ? formatter(value, child.format) : value
        }
      }
    })
  }, [data, childrenItems, formatter])

  useEffect(() => {
    setSeriesLoading(seriesProps.seriesId, isLoading)
  }, [seriesProps.seriesId, isLoading, setSeriesLoading])

  if (isLoading) return null

  return (
    <>
      <ChartGraphic
        {...seriesProps}
        type={type}
        {...(graphicText ? {
          style: {
            fontFamily: 'GothamPro, Gotham-Book, "Open Sans", Tahoma, "Segoe UI", sans-serif',
            fill: '#000',
            ...(seriesProps.style ?? {}),
            text: graphicText
          }
        } : {})}
        {...(graphicChildren ? { children: graphicChildren } : {})}
      />
    </>
  )
}

PipelineChartGraphic.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,
  type: PropTypes.oneOf([
    'image', 'text', 'rect', 'circle', 'ring', 'sector', 'arc',
    'polygon', 'polyline', 'line', 'bezierCurve', 'group'
  ]),
  valueAccessor: PropTypes.string,
  valueFormat: PropTypes.string,
  childrenItems: PropTypes.arrayOf(PropTypes.shape({
    accessor: PropTypes.string,
    text: PropTypes.string,
    format: PropTypes.string
  }))
}

PipelineChartGraphic.defaultProps = {
  valueFormat: 'marketValue',
  categoryFormat: 'date',
  height: 300
}

export default PipelineChartGraphic
